import React, { useEffect } from 'react'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { Link } from 'react-router-dom'

import { Box, FormHelperText, FormLabel } from '@material-ui/core'

import search from '../../../assets/search.svg'

import { MyServerTable } from '../../MyServerTable'
import { BootstrapInput } from '../../BootstrapInput'
import { ReactiveTab, ReactiveTabs } from '../../ReactiveTabs'
import { PullOrdersWithErrorsButton } from '../../PullOrdersWithErrorsButton'
import { OrdersTableTab, ordersTableTabNames } from '../../StoreOrdersScreen'

import { useLazyEffect } from '../../../hooks/commonHooks'
import { useLogic, useStore } from '../../../hooks/storeHook'

import { useOrderSearchStyles } from '../../../hooks/ordersSearchStylesHook'
import { useOrdersTableStyles } from '../../../hooks/ordersTableStylesHook'

import { appRoutes } from '../../../common/appRoutes'

import { useMoySkladOrderColumns } from '../../../types/moySkladOrderColumns'

const tabValues =
  [OrdersTableTab.Active, OrdersTableTab.WithErrors] as const satisfies readonly OrdersTableTab[]

const tabs =
  tabValues.map(value => ({value, title: ordersTableTabNames[value]})) satisfies ReactiveTab<OrdersTableTab>[]

export const MoySkladOrdersScreen =
  observer(() => {
    const {orderStore: {moySkladOrders}, syncStore: {moySkladAccount}} = useStore()
    const moySkladOrderColumns = useMoySkladOrderColumns()

    const classes = useOrderSearchStyles()
    const tableClasses = useOrdersTableStyles()

    const {api, msOrderLoaders} = useLogic()

    // State is stored in mobx. If UI overrides it, use local state like in StoreOrdersScreen.
    useLazyEffect(() => moySkladOrders.setPropFilter('hasError', JSON.stringify(false)))

    useEffect(() => msOrderLoaders.addHandler(moySkladOrders.load), [msOrderLoaders, moySkladOrders])

    const selectedTab =
      (moySkladOrders.findPropFilterValue('hasError') === JSON.stringify(true)
       ? OrdersTableTab.WithErrors
       : OrdersTableTab.Active) satisfies ((typeof tabValues)[number])

    const onTabChange =
      (newTab: OrdersTableTab, _event: React.ChangeEvent<{}>) => {
        runInAction(() => {
          moySkladOrders.resetPropFilters()
          moySkladOrders.setPropFilter('hasError', JSON.stringify(newTab === OrdersTableTab.WithErrors))
          moySkladOrders.reload(true)
        })
      }

    if (moySkladAccount == null) {
      return (
        <Box padding={3}>
          Для работы с заказами добавьте <Link to={appRoutes.MoySklad.settings}>аккаунт МоегоСклада</Link>.
        </Box>
      )
    }

    return (
      <Box marginTop={'-40px'}>
        <ReactiveTabs value={selectedTab} tabs={tabs} onChange={onTabChange}>
          <Box
            marginY={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            className={classes.container}>
            <Box position="relative">
              <Box minWidth={350} maxWidth={350}>
                <FormLabel className={classes.root}>Поиск</FormLabel>

                <BootstrapInput
                  fullWidth
                  onChange={e => moySkladOrders.setGlobalFilter(e.target.value)}
                  inputProps={{'aria-label': 'search ms orders'}}
                />

                <FormHelperText className={classes.root}>
                  Id / GUID в МоёмСкладе / Id в Ozon / Id в Wildberries / Id в Yandex.Market
                </FormHelperText>
              </Box>

              <img className="search-icon" src={search} alt="Поиск" style={{top: 38, right: 20}}/>
            </Box>

            {
              selectedTab === OrdersTableTab.WithErrors &&
              <PullOrdersWithErrorsButton
                ordersTable={moySkladOrders}
                pullOrders={api.order.pullMoySkladOrdersWithErrors}
              />
            }
          </Box>

          <MyServerTable
            classes={tableClasses}
            table={moySkladOrders}
            columns={moySkladOrderColumns}
            labelNoRows="Заказов нет"
            labelNotFound="Заказы не найдены"
            labelRowsPerPage="Заказов на странице:"
          />
        </ReactiveTabs>
      </Box>
    )
  })